import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faSearch,
  faUpload,
  faX,
} from '@fortawesome/free-solid-svg-icons';
import { withAppHOC } from '../../hoc';
import {
  StyledContentContainer,
  StyledActionItemButtons,
  StyledFiltersContainer,
  StyledFilters,
  StyledFilterContainer,
  StyledSearchButton,
  StyledResetButton,
  StyledStatusPill,
  StyledModalContainer,
  StyledFormContainer,
  StyledEditItemButton,
} from '../../components/styled';
import PageHeader from '../../components/PageHeader/PageHeader';
import {
  Button,
  Fieldset,
  Form,
  Label,
  MessageBox,
  TextInput,
} from '../../components/FormElements';
import Loader from '../../components/Loader/Loader';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import NoData from '../../components/NoData/NoData';
import { TableBuilder } from '../../components/TableElements';
import Pagination from '../../components/Pagination/Pagination';
import API from '../../api';
import { RESPONSE_STATUSES } from '../../constants';
import { formatCurrency, getBase64, getSearchParams } from '../../utils';
import FiltersButton from '../../components/FiltersButton/FiltersButton';
import Modal from '../../components/Modal';

const SellerInvoicesList = () => {
  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };
  const [filters, setFilters] = useState({
    date: '',
    year: '',
    month: '',
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams('pageNumber') || 1,
    recordsPerPage: getSearchParams('recordsPerPage') || 30,
  });
  const [modalState, setModalState] = useState({
    showModal: false,
    id: '',
    isLoading: false,
    status: '',
    message: '',
    file: '',
  });

  const handleFilterChange = (key, value) => {
    const splitValue = value && value.split('-');

    setFilters((prev) => ({
      ...prev,
      [key]: value,
      year: splitValue[0] || '',
      month: splitValue[1] || '',
    }));
  };

  const readData = async () => {
    try {
      const response = await API.get(
        `/invoicing/seller?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&year=${appliedFilters.year}&month=${appliedFilters.month}`
      );
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ['seller_invoices', paginationData, appliedFilters],
    queryFn: readData,
  });

  const reloadData = async () => await refetch();

  const filterData = async (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
  };

  const clearFilters = async (e) => {
    e.preventDefault();
    setFilters({
      date: '',
      year: '',
      month: '',
    });
    setAppliedFilters({
      date: '',
      year: '',
      month: '',
    });
  };

  const uploadInvoice = async (e) => {
    e.preventDefault();
    setModalState((prevState) => ({
      ...prevState,
      isLoading: true,
      state: '',
      message: '',
    }));
    try {
      const response = await API.put(`/invoicing/seller/${modalState?.id}`, {
        file: modalState.file,
      });
      const { status, message } = response.data;
      setModalState((prevState) => ({
        ...prevState,
        status: status,
        message: message,
      }));
      if (status === RESPONSE_STATUSES.SUCCESS) {
        reloadData();
        setTimeout(() => {
          setModalState((prevState) => ({
            ...prevState,
            showModal: false,
            id: '',
            isLoading: false,
            status: '',
            message: '',
            file: '',
          }));
        }, 3000);
      }
    } catch (error) {
      setModalState((prevState) => ({
        ...prevState,
        status: RESPONSE_STATUSES.FAILED,
        message: error.message,
      }));
    } finally {
      setModalState((prevState) => ({
        ...prevState,
        isLoading: true,
      }));
    }
  };

  const renderPageHeading = () => (
    <PageHeader
      title="Seller Invoices"
      showReloadData={true}
      reloadData={reloadData}
    />
  );

  if (isLoading) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <LoadingSpinner />
        </StyledContentContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </StyledContentContainer>
      </>
    );
  }

  return (
    <>
      <Loader isLoading={isRefetching} />
      {renderPageHeading()}
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledContentContainer graybackground="true">
        <StyledFiltersContainer className={showFilters ? 'show' : ''}>
          <StyledFilters>
            <StyledFilterContainer>
              <TextInput
                type="month"
                value={filters.date}
                onChange={(value) => handleFilterChange('date', value)}
                placeholder="Year & Month"
                disabled={isLoading}
              />
            </StyledFilterContainer>
            <StyledSearchButton onClick={filterData} type="button">
              <FontAwesomeIcon icon={faSearch} />
              Search
            </StyledSearchButton>
            <StyledResetButton onClick={clearFilters} type="button">
              <FontAwesomeIcon icon={faX} />
              Clear
            </StyledResetButton>
          </StyledFilters>
        </StyledFiltersContainer>
        {data?.status === RESPONSE_STATUSES.FAILED && (
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={data?.message}
          />
        )}
        {data?.data?.length === 0 ? (
          <NoData />
        ) : (
          <>
            <TableBuilder
              isLoading={isLoading}
              tableHeadings={[
                {
                  title: 'Year',
                  dataSelector: 'year',
                  dataType: 'string',
                },
                {
                  title: 'Month',
                  dataSelector: 'month',
                  dataType: 'string',
                },
                {
                  title: 'Amount',
                  dataSelector: 'amount',
                  dataType: 'number',
                  cellrenderer: (value) =>
                    value ? formatCurrency(value) : 'N/A',
                },
                {
                  title: 'Remark',
                  dataSelector: 'remark',
                  dataType: 'string',
                  cellrenderer: (value) => (value ? value : 'N/A'),
                },
                {
                  title: 'Status',
                  dataSelector: 'status',
                  dataType: 'string',
                  cellrenderer: (value) => (
                    <StyledStatusPill
                      className={
                        value === 'Approved'
                          ? 'green'
                          : value === 'Rejected'
                          ? 'red'
                          : value === 'Submitted'
                          ? 'blue'
                          : 'yellow'
                      }
                    >
                      {value}
                    </StyledStatusPill>
                  ),
                },
                {
                  title: 'GSTR1 Status',
                  dataSelector: 'gstr1Status',
                  dataType: 'string',
                  cellrenderer: (value) => (
                    <StyledStatusPill
                      className={
                        value === 'Approved'
                          ? 'green'
                          : value === 'Rejected'
                          ? 'red'
                          : value === 'Submitted'
                          ? 'blue'
                          : 'yellow'
                      }
                    >
                      {value}
                    </StyledStatusPill>
                  ),
                },
                {
                  title: '',
                  dataSelector: 'status',
                  canSort: false,
                  isSticky: 'Yes',
                  stickyAlign: 'right',
                  isDummy: 'Yes',
                  textAlign: 'center',
                  width: 'max-content',
                  cellrenderer: (value, item) =>
                    !['Submitted', 'Approved'].includes(value) ? (
                      <StyledActionItemButtons
                        onClick={() => {
                          setModalState((prevState) => ({
                            ...prevState,
                            showModal: true,
                            id: item.id,
                            isLoading: false,
                            status: '',
                            message: '',
                            file: '',
                          }));
                        }}
                      >
                        <FontAwesomeIcon icon={faUpload} /> Upload
                      </StyledActionItemButtons>
                    ) : item.invoiceFile ? (
                      <StyledActionItemButtons>
                        <StyledEditItemButton
                          to={item.invoiceFile}
                          title="Download"
                        >
                          <FontAwesomeIcon icon={faDownload} /> Download
                        </StyledEditItemButton>
                      </StyledActionItemButtons>
                    ) : (
                      <></>
                    ),
                },
              ]}
              tableData={data?.data}
            />
            <Pagination
              totalRecords={data?.totalRecords}
              paginationData={paginationData}
              setPaginationData={setPaginationData}
            />
            <Modal
              modalStatus={modalState.showModal}
              setModalStatus={() => {
                setModalState((prevState) => ({
                  ...prevState,
                  showModal: false,
                  id: '',
                  isLoading: false,
                  status: '',
                  message: '',
                  file: '',
                }));
              }}
              hideCloseButton
            >
              <StyledModalContainer width={`500px`}>
                <h3>Upload Invoice PDF</h3>
                <StyledFormContainer>
                  <Form method="POST" action="#" onSubmit={uploadInvoice}>
                    <Fieldset>
                      <Label required>Invoice File</Label>
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            getBase64(file, (base64) => {
                              setModalState((prevState) => ({
                                ...prevState,
                                file: base64,
                              }));
                            });
                          }
                        }}
                        placeholder="Please select invoice pdf"
                        disabled={modalState.isLoading}
                      />
                    </Fieldset>
                    {modalState.showModal && modalState.status !== '' && (
                      <Fieldset>
                        <MessageBox
                          status={modalState.status}
                          message={modalState.message}
                        />
                      </Fieldset>
                    )}
                    <Fieldset>
                      <Button
                        disabled={modalState.isLoading}
                        type="submit"
                        style={{ width: 'auto' }}
                      >
                        Upload
                      </Button>
                    </Fieldset>
                    <Loader isLoading={modalState.isLoading} />
                  </Form>
                </StyledFormContainer>
              </StyledModalContainer>
            </Modal>
          </>
        )}
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(SellerInvoicesList);
